import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, AlertTriangle, Signal, Clock, User } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useUserCheck } from '../hooks/api/useUserCheck'
import { useTicket, useUpdateTicket } from '../hooks/api/useTickets'
import { useCompanyUsers } from '../hooks/api/useUsers'
import { DashboardLayout } from '../components/layout/DashboardLayout'
import { Select } from '../components/ui/Select'

type TicketStatus = 'open' | 'in_progress' | 'waiting' | 'resolved' | 'closed'

const STATUS_OPTIONS: { value: TicketStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' }
]

export const IssueDetailPage = () => {
  const { issueId } = useParams<{ issueId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: userRecord, isLoading, error } = useUserCheck(user?.id || '')
  const { data: ticket, isLoading: ticketLoading, error: ticketError } = useTicket(issueId || '')
  const updateTicketMutation = useUpdateTicket()
  const { data: companyMembers = [], isLoading: membersLoading } = useCompanyUsers(ticket?.company_id)

  const [status, setStatus] = useState<TicketStatus>('open')
  const [assignedTo, setAssignedTo] = useState<string>('')
  const [resolutionNotes, setResolutionNotes] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [formSuccess, setFormSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (ticket) {
      setStatus((ticket.status as TicketStatus) || 'open')
      setAssignedTo(ticket.assigned_to_user_id || '')
      setResolutionNotes(ticket.resolution_notes || '')
    }
  }, [ticket])

  const handleUpdateTicket = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!ticket) return

    setFormError(null)
    setFormSuccess(null)

    try {
      const trimmedNotes = resolutionNotes.trim()
      await updateTicketMutation.mutateAsync({
        ticketId: ticket.id,
        updates: {
          status,
          assigned_to_user_id: assignedTo ? assignedTo : null,
          resolution_notes: trimmedNotes ? trimmedNotes : undefined
        }
      })
      setFormSuccess('Ticket updated successfully.')
      setTimeout(() => setFormSuccess(null), 4000)
    } catch (mutationError) {
      console.error('IssueDetailPage: failed to update ticket', mutationError)
      setFormError('Unable to update ticket. Please try again.')
    }
  }

  if (isLoading || ticketLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-400 border-t-transparent border-r-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading issue details...</p>
        </div>
      </div>
    )
  }

  if (error || ticketError || !ticket || !userRecord) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-red-600">Issue not found or an error occurred.</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
      case 'urgent':
        return 'bg-red-100 text-red-800'
      case 'medium':
        return 'bg-orange-100 text-orange-800'
      case 'low':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-gray-100 text-gray-800 border border-gray-300'
      case 'in_progress':
        return 'bg-blue-100 text-blue-800'
      case 'waiting':
        return 'bg-yellow-100 text-yellow-800'
      case 'resolved':
        return 'bg-green-100 text-green-800'
      case 'closed':
        return 'bg-gray-100 text-gray-600'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <DashboardLayout 
      userRole={userRecord.role} 
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">Issue Details</h1>
        </div>

        {/* Issue Overview Card */}
        <div className="bg-white p-6">
          <div className="mb-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <AlertTriangle className="w-6 h-6" />
                  <h2 className="text-xl font-semibold text-gray-900">{ticket.title}</h2>
                </div>
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                  <Signal className="w-3 h-3 " />
                  {ticket.priority === 'high' ? 'High' : ticket.priority === 'medium' ? 'Medium' : 'Low'}
                </span>
              </div>
          </div>

          <div className='p-8 border border-gray-200 bg-gray-50 rounded-xl'>
            {/* Description */}
          <div className="mb-6">
            <div className="flex items-center space-x-4 mb-4">
              <span className="text-sm text-gray-500">#{ticket.id.slice(-4).toUpperCase()}</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                {ticket.status}
              </span>
            </div>
            <p className="text-gray-400 leading-relaxed">{ticket.description}</p>
          </div>

          {/* Steps Taken So Far */}
          {ticket.steps_taken && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-400 mb-3">Steps Taken So Far</h3>
              <ul className="space-y-2">
                {ticket.steps_taken.split('\n').map((step, index) => (
                  <li key={index} className="flex items-start">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-gray-400">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Impact */}
          {ticket.impact && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-400 mb-2">Impact</h3>
              <p className="text-gray-400 leading-relaxed">{ticket.impact}</p>
            </div>
          )}

          {/* Screenshots/Attachments */}
          {ticket.attachments && ticket.attachments.length > 0 && (
            <div className="mb-6">
              <div className="space-y-4">
                {ticket.attachments.map((attachment, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <div className="bg-white border border-gray-300 rounded-lg p-6 max-w-md mx-auto">
                        <div className="text-red-600 font-semibold mb-2">The backup failed.</div>
                        <div className="text-sm text-gray-400 mb-2">
                          There was a failure in preparing the backup image of one of the volumes in the backup set. (0x807800C5)
                        </div>
                        <div className="text-xs text-gray-600 mb-4">
                          Additional Information: The process cannot access the file because it is being used by another process. (0x80070020)
                        </div>
                        <button className="bg-gray-200 text-gray-800 px-4 py-1 rounded text-sm">
                          Close
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 text-center">{attachment.filename}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error Logs */}
          {ticket.error_logs && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Error Logs</h3>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                <pre className="whitespace-pre-wrap">{ticket.error_logs}</pre>
              </div>
            </div>
          )}

          {/* Resolution Notes */}
          {ticket.resolution_notes && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Resolution Notes</h3>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800">{ticket.resolution_notes}</p>
              </div>
            </div>
          )}
          </div>

          {/* Metadata Footer */}
          <div className="border-t border-gray-200 pt-4 mt-6">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  <span>Submitted by: {ticket.created_by_email || 'Unknown'}</span>
                </div>
                {ticket.assigned_to_email && (
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    <span>Assigned to: {ticket.assigned_to_email}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                <span>{formatDate(ticket.created_at)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Controls */}
        {userRecord.role === 'admin' && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Manage Ticket</h3>
            <form className="space-y-6" onSubmit={handleUpdateTicket}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <Select
                    value={status}
                    onChange={(event) => setStatus(event.target.value as TicketStatus)}
                    className="w-full"
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assign To</label>
                  <Select
                    value={assignedTo}
                    onChange={(event) => setAssignedTo(event.target.value)}
                    className="w-full"
                    disabled={membersLoading}
                  >
                    <option value="">Unassigned</option>
                    {companyMembers.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.full_name || member.email}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Notes</label>
                <textarea
                  value={resolutionNotes}
                  onChange={(event) => setResolutionNotes(event.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
                  placeholder="Add internal notes or the resolution summary for this ticket."
                />
              </div>

              {(formError || formSuccess) && (
                <div
                  className={`rounded-lg px-4 py-3 text-sm ${
                    formSuccess
                      ? 'border border-green-200 bg-green-50 text-green-700'
                      : 'border border-red-200 bg-red-50 text-red-600'
                  }`}
                >
                  {formSuccess || formError}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="inline-flex items-center rounded-full bg-gray-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-70"
                  disabled={updateTicketMutation.isPending}
                >
                  {updateTicketMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
